const DEMO_HOSTNAMES = ['demo.tst.datanomik.com', 'demo-beta.tst.datanomik.com'];
const DEMO_BASE = 'https://backentira.vercel.app';

/** Should we override API URLs to the demo backend? Only for the demo hostnames. */
const shouldOverrideUrls = (): boolean => {
  try {
    const { hostname } = window.location;
    return DEMO_HOSTNAMES.some((demoHostname) => hostname.includes(demoHostname));
  } catch {
    return false;
  }
};

/** Does this URL point at the real production API (not TST)? */
const pointsToProd = (url?: string): boolean =>
  !!url && url.includes('dashboard-api.datanomik.com') && !url.includes('.tst.');

/**
 * True when a LOCAL DEV session (any hostname/port) is talking to the real
 * production API — used to show the PROD warning badge.
 *
 * Works in both flavors of dev:
 *  - proxy mode (default `dev:prod`): API URLs are relative, so we key off
 *    VITE_PROXY_TARGET (the upstream the Vite proxy forwards to);
 *  - direct mode (`dev:prod-direct`): API URLs are absolute prod URLs.
 *
 * Gated on `import.meta.env.DEV` so the badge never shows on the deployed prod
 * build (where DEV is false), and is hostname-agnostic so it survives serving
 * on `<subdomain>.localhost` instead of `localhost`.
 */
export const isDevPointingAtProd = (): boolean => {
  try {
    if (!import.meta.env.DEV) return false;
    const proxyTarget = import.meta.env.VITE_PROXY_TARGET as string | undefined;
    const apiUrl = import.meta.env.VITE_DASHBOARD_API_URL as string | undefined;
    return pointsToProd(proxyTarget) || pointsToProd(apiUrl);
  } catch {
    return false;
  }
};

/** Is this a demo environment? (UI indicator — badge, limited features, etc.) */
export const isDemo = (): boolean => {
  try {
    if (import.meta.env.VITE_DEMO === 'true') return true;
    if (shouldOverrideUrls()) return true;

    const apiUrl = import.meta.env.VITE_DASHBOARD_API_URL as string;
    if (apiUrl && (apiUrl.includes('backentira') || apiUrl.includes('localhost:3000'))) return true;

    return false;
  } catch {
    return false;
  }
};

export const getApiUrls = () => {
  if (shouldOverrideUrls()) {
    return {
      DASHBOARD_MGR_URL: `${DEMO_BASE}/v1/`,
      DASHBOARD_MGR_JWT_URL: `${DEMO_BASE}/jwt/`,
      DASHBOARD_MGR_API_URL: `${DEMO_BASE}/api/v1/`,
      DASHBOARD_API_URL: `${DEMO_BASE}/api/v1/`,
      BIRT_URL: `${DEMO_BASE}/report-viewer`,
    };
  }

  return {
    DASHBOARD_MGR_URL: import.meta.env.VITE_DASHBOARD_MGR_URL as string,
    DASHBOARD_MGR_JWT_URL: import.meta.env.VITE_DASHBOARD_MGR_JWT_URL as string,
    DASHBOARD_MGR_API_URL: import.meta.env.VITE_DASHBOARD_MGR_API_URL as string,
    DASHBOARD_API_URL: import.meta.env.VITE_DASHBOARD_API_URL as string,
    BIRT_URL: import.meta.env.VITE_BIRT_URL as string,
  };
};

const apiUrls = getApiUrls();

export default apiUrls;
